import React, { useCallback, useContext, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import styles from "./ProcessPanelLiveMode.module.css";
import { AgendaItem } from "src/types";
import { UI } from "@wwimmo/ui";

const ProcessPanelLiveModeBase = observer(() => {
    const { agendaitemStore, assemblyStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const currentItem = agendaitemStore.currentAgendaItem;

    const onClickAgendaItem = useCallback(
        (item: AgendaItem) => async (e: React.MouseEvent) => {
            e.preventDefault();
            assemblyStore.setIsCloseLiveModeTabActive(false);
            assemblyStore.setIsAttendanceLiveModeTabActive(false);
            agendaitemStore.setCurrentAgendaItem(item);
        },
        [agendaitemStore, assemblyStore]
    );

    const onCloseClick = useCallback(() => {
        assemblyStore.setIsCloseLiveModeTabActive(true);
        assemblyStore.setIsAttendanceLiveModeTabActive(false);
        agendaitemStore.setCurrentAgendaItem(undefined);
    }, [assemblyStore, agendaitemStore]);

    const onAttendancelistClick = useCallback(() => {
        assemblyStore.setIsAttendanceLiveModeTabActive(true);
        assemblyStore.setIsCloseLiveModeTabActive(false);
        agendaitemStore.setCurrentAgendaItem(undefined);
    }, [assemblyStore, agendaitemStore]);

    return (
        <div
            className={`${styles.ProcessPanel} ${isCollapsed ? styles.ProcessPanelCollapsed : ""}`}
            onClick={isCollapsed ? () => setIsCollapsed(false) : undefined}
        >
            <ul className={`${styles.AgendaList} ${isCollapsed ? styles.AgendaListCollapsed : ""}`}>
                <li
                    className={`${styles.HeaderItem} ${
                        assemblyStore.isAttendanceLiveModeTabActive ? styles.ActiveItem : ""
                    }`}
                    onClick={onAttendancelistClick}
                >
                    {t("screens.assemblies.attendanceList")}
                </li>

                {agendaitemStore.currentAgendaItems.map((parent) => (
                    <React.Fragment key={parent.id}>
                        <li
                            className={`${styles.ParentItem} ${currentItem?.id === parent.id ? styles.ActiveItem : ""}`}
                            onClick={onClickAgendaItem(parent)}
                        >
                            {isCollapsed ? (
                                <span className={styles.CollapsedSort}>{parent.sort}.</span>
                            ) : (
                                <>
                                    <div className={styles.TextClamp}>
                                        {parent.sort}. {parent.text}
                                    </div>
                                    {parent.hasVotings ? (
                                        <UI.Icon icon={UI.SVGIcon.Voting} className={styles.VotingIcon} size={28} />
                                    ) : null}
                                </>
                            )}
                        </li>
                        {parent.agendaitemchild?.map((child) => (
                            <li
                                key={child.id}
                                className={`${styles.ChildItem} ${
                                    currentItem?.id === child.id ? styles.ActiveItem : ""
                                }`}
                                onClick={onClickAgendaItem(child)}
                            >
                                {isCollapsed ? (
                                    <span className={styles.CollapsedSort}>
                                        {parent.sort}.{child.sort}
                                    </span>
                                ) : (
                                    <>
                                        <div className={styles.TextClamp}>
                                            {parent.sort}.{child.sort} {child.text}
                                        </div>
                                        {child.hasVotings ? (
                                            <UI.Icon icon={UI.SVGIcon.Voting} className={styles.VotingIcon} size={28} />
                                        ) : null}
                                    </>
                                )}
                            </li>
                        ))}
                    </React.Fragment>
                ))}

                <li
                    className={`${styles.FooterItem} ${
                        assemblyStore.isCloseLiveModeTabActive ? styles.ActiveItem : ""
                    }`}
                    onClick={onCloseClick}
                >
                    {t("screens.assemblies.livemode.close")}
                </li>
            </ul>
            <button className={styles.CollapseButton} onClick={() => setIsCollapsed((v) => !v)}>
                <UI.Icon icon={isCollapsed ? UI.SVGIcon.ChevronRight : UI.SVGIcon.ChevronLeft} size={24} />
            </button>
        </div>
    );
});

export const ProcessPanelLiveMode = Sentry.withProfiler(ProcessPanelLiveModeBase);
